import { useBillingIsTracks } from 'src/hooks/useBillingIsTracks';
import { useFeatureFlags } from 'feature-flags';
import { Navigate, lazyRouteComponent } from 'src/lib/routing';
import { RequireModeratedScheduling } from 'src/route-guards';

import { RequireAuth } from 'containers/Auth/RequireAuth';
import { useConnectedClient } from 'context/ConnectedClientContext';
import { teamUtils } from 'lib/teams';

import type { ReactNode } from 'react';
import type { RouteObject } from 'src/lib/routing';

const SettingsContainer = lazyRouteComponent(() => import('./SettingsContainer'), 'SettingsContainer');
const SettingsAccount = lazyRouteComponent(() => import('./containers/Account/Account'), 'SettingsAccount');
const SettingsBilling = lazyRouteComponent(() => import('./containers/Billing/Billing'), 'SettingsBilling');
const SettingsIntegrations = lazyRouteComponent(
  () => import('./containers/Integrations/Integrations'),
  'SettingsIntegrations',
);
const SettingsInvoices = lazyRouteComponent(() => import('./containers/Invoices/Invoices'), 'SettingsInvoices');
const SettingsMembers = lazyRouteComponent(() => import('./containers/Members/Members'), 'SettingsMembers');
const SettingsNotifications = lazyRouteComponent(
  () => import('./containers/Notifications/Notifications'),
  'SettingsNotifications',
);
const SettingsResources = lazyRouteComponent(() => import('./containers/Resources/Resources'), 'SettingsResources');
const SettingsTeam = lazyRouteComponent(() => import('./containers/Team/Team'), 'SettingsTeam');
const EnterpriseSSO = lazyRouteComponent(
  () => import('./containers/Team/containers/EnterpriseSSO/EnterpriseSSO'),
  'EnterpriseSSO',
);
const SettingsUsage = lazyRouteComponent(() => import('./containers/Usage/Usage'), 'SettingsUsage');
const SettingsTags = lazyRouteComponent(() => import('./containers/Tags/Tags'), 'SettingsTags');

// Permission gating that used to be conditional route-array inclusion. The
// route tree is static now (single TanStack router), so these read the same
// values at render time and fall through to `/not-found` — the same place
// `RouteProtection` sends disallowed routes — when access isn't granted.
const RequireTeamAdmin = ({ children }: { children: ReactNode }) => {
  const { details } = useConnectedClient();
  return teamUtils.isAdminByRoleId(details?.teamRole) ? <>{children}</> : <Navigate to="/not-found" replace />;
};

const RequireUsageBilling = ({ children }: { children: ReactNode }) => {
  // `usage` / `invoices` only exist for teams not on the tracks billing model.
  const { enabled: isBillingTracksEnabled } = useBillingIsTracks();
  return isBillingTracksEnabled ? <Navigate to="/not-found" replace /> : <>{children}</>;
};

const RequireClientsTranscript = ({ children }: { children: ReactNode }) => {
  const { clients_transcript } = useFeatureFlags(['clients_transcript']);
  return clients_transcript ? <>{children}</> : <Navigate to="/not-found" replace />;
};

export const settingsRoutes: RouteObject[] = [
  {
    path: 'settings',
    element: (
      <RequireAuth>
        <SettingsContainer />
      </RequireAuth>
    ),
    children: [
      { path: 'account', element: <SettingsAccount /> },
      { path: 'notifications', element: <SettingsNotifications /> },
      {
        path: 'integrations',
        element: (
          <RequireModeratedScheduling>
            <SettingsIntegrations />
          </RequireModeratedScheduling>
        ),
      },
      { path: 'team', element: <SettingsTeam /> },
      { path: 'members', element: <SettingsMembers /> },
      { path: 'billing', element: <SettingsBilling /> },
      {
        path: 'usage',
        element: (
          <RequireUsageBilling>
            <SettingsUsage />
          </RequireUsageBilling>
        ),
      },
      {
        path: 'invoices',
        element: (
          <RequireUsageBilling>
            <SettingsInvoices />
          </RequireUsageBilling>
        ),
      },
      { path: 'billing/usage', element: <Navigate to="/settings/usage" replace /> },
      { path: 'billing/invoice', element: <Navigate to="/settings/invoices" replace /> },
      { path: 'resources', element: <SettingsResources /> },
      {
        path: 'tags',
        element: (
          <RequireClientsTranscript>
            <SettingsTags />
          </RequireClientsTranscript>
        ),
      },
      {
        path: 'sso',
        element: (
          <RequireTeamAdmin>
            <EnterpriseSSO />
          </RequireTeamAdmin>
        ),
      },
      { index: true, element: <Navigate to="/settings/account" replace /> },
    ],
  },
];
